/* ── 128 §4 — ONE PRESENTATION, TWO MOUNTS ─────────────────────────────────
 *
 * [RULED — Laurent, glancing #432] "the Bell lacks the nice sections of the
 * Today popup — why the difference, couldn't Bell and Today be the same, based
 * on Today now which is better than bell?" Yes, and Today's is the one that
 * wins.
 *
 * 126 §4b gave the two surfaces ONE LIST: `allDO` IS `notifications`, one
 * exhaustive `groupOf()` whose return type has no `undefined` in it, and a
 * DEFAULT group that catches whatever no heading claims. This gives them ONE
 * PRESENTATION, and it undoes nothing of that — the partition below is 126
 * §4b's, moved rather than rewritten.
 *
 * IT IS THE SAME MOVE `NotifRowView` MADE IN 125 §6, one level up. That slice
 * ended two renderers of a ROW; this ends two renderers of a LIST. The bell
 * was `notifications.slice(0, 20).map(…)` — one flat list, no headings, no
 * counters — and this modal was four `SEC`s over a partition computed in its
 * own render body. Two answers to "how is this list presented", and the reader
 * saw both of them within one click of each other.
 *
 * ── WHAT THE BRIEF'S OWN TABLE GOT WRONG, MEASURED IN A BROWSER AT 9f5c3da3 ─
 *
 * §4's table was transcribed from two screenshots, and SESSION_RULES says why
 * that is a hazard ("a coordinate inside one is transcribed rather than
 * copied"). Three of its four rows were already true on the tree:
 *
 *   the day's quote        brief: bell bottom.  MEASURED: TOP in both. 126 §3
 *                          moved it and this file inherits that.
 *   the "why it cannot be  brief: not shown in bell. MEASURED: shown in both —
 *   dismissed" reason      it lives in `NotifRowView`, which both mount, and
 *                          the bell dump carries "disappears when the day has
 *                          passed" five times.
 *   the sign-in line       brief: bottom / bottom. Correct.
 *   grouped sections with  brief: overview only. CORRECT, and it is the whole
 *   counters               of this §.
 *
 * ── THE CAP IS GONE, AND THAT IS A CONSEQUENCE RATHER THAN A CHOICE ────────
 *
 * The bell capped at `slice(0, 20)`. §4 rules that the LIST, its GROUPING, its
 * COUNTERS and its ROWS may not differ between the two mounts — and a COUNTER
 * OVER A CAPPED LIST IS A LIE: "REMINDERS (12)" over eight rows because the
 * cap ate four is worse than no counter at all, because it is a number the
 * reader has no way to doubt. Either both surfaces cap (and both lie) or
 * neither does. The bell already scrolls (`maxHeight: 600, overflowY: auto`)
 * and, since 128 §1, so does the overview — so neither needs a cap to stay on
 * the screen.
 *
 * ── WHAT LEGITIMATELY DIFFERS, AND WHY IT MUST ─────────────────────────────
 *
 * This component is the list and nothing else. The chrome around it stays each
 * surface's own, because a dropdown and a modal are different objects: their
 * width, their border, and how you dismiss them. So the bell keeps its header
 * and "Mark all read" (the overview opens once per sign-in and has no use for
 * a bulk read), the overview keeps "Remove all" and "Don't show again today"
 * and the Outlook-import prompt, and each passes its own `onActed`. None of
 * that is the list.
 */
import type React from "react";
import NotifRowView from "./NotifRowView";
import { FS } from "../lib/tokens";
import type { NotifRow as NotifRowFace } from "../utils";
import type { TFn } from "../lib/taxonomyTypes";
import { bellGroups, type BellPlace } from "../lib/bellPlace"; // 209 §26

/* The list this renders, as the aggregator builds it. Structural and
   deliberately narrow — the fields the PARTITION reads, plus the `id` the key
   needs. `NotifRowView` describes what a ROW needs; describing it twice is how
   two files come to disagree about one object. */
export type GroupedRow = NotifRowFace & {
  type?: string | null;
  ts?: string | null;
  /** 209 §26 — where the row lands, stated where the aggregator builds it. */
  _bell?: BellPlace;
};


/* ── 209 §26 [Laurent] — TWO GROUPS: TODAY'S, THEN WHAT IS LATE ─────────────
 *
 * « The Bell popout is overcrowded with old notifications, reminders, etc.
 * Should show only (a) the notifications of the day …, and under, (b) the
 * tasks which are late. » 126 §4b's four groups (Overdue · Due today ·
 * Reminders · Also — `groupOf`, which read each row's `type` and `ts`) are two:
 * (a) « Aujourd'hui » — every row whose day is today, and tomorrow's events
 * after them (addendum 6) — and (b) the overdue group as it stood, under it.
 * What left the bell left at its source (the aggregator, `bellRows`), so this
 * partition sees only rows that stay, and it reads the place each row STATES
 * (`_bell`) rather than re-deriving it from `type` and `ts` — `bellGroups`
 * (lib/bellPlace.ts), total as 126 §4b's was: a row that states no place
 * prints under (a). */

/* ── BUG-A SWEEP — `SEC` came here from App.tsx module scope ───────────────
 *
 * It had been hoisted out of the daily-overview modal's render body by the
 * 2026-07-27 sweep (it was `<SEC …/>` declared inside a render, so every App
 * render gave React a new component type for each section). It stays at module
 * scope here for the same reason; `scripts/lint-inline-components.js` enforces
 * it either way.
 *
 * ── BRIEF 125 §6 — IT IS THE GROUPING AND NOTHING ELSE ────────────────────
 *
 * Laurent: "the login reminder panel is useless, shows only task descriptions
 * without knowing the case: just let it show the exact content of the bell
 * menu." This block WAS a second row renderer — `n.icon`, `n.msg`, `n.ref` and
 * a date, no type badge, no matter title, no Dismiss, no Join & time. It
 * mounts `NotifRowView` now, the same component the bell mounts.
 *
 * THE ONE COLLISION, AND THE ROW WINS. This block coloured the icon with its
 * SECTION's colour; the row colours it from `n.type`. "The row may not fork"
 * decides it — the heading keeps `color` (framing), the icon takes the row's
 * own mapping. A DUE TODAY event shows the blue event glyph under an amber
 * heading, which is what the bell has always shown for that notification. */
function SEC({
  title,
  color,
  items,
  row,
}: {
  title: string;
  color: string;
  items: GroupedRow[];
  row: RowProps;
}) {
  if (!items.length) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: FS.s10,
          fontWeight: 600,
          color,
          textTransform: "uppercase",
          letterSpacing: ".3px",
          marginBottom: 4,
        }}
      >
        {title} ({items.length})
      </div>
      {items.map((n) => (
        <NotifRowView
          key={String(n.id)}
          {...row}
          n={n}
          isRead={row.isNotifReadFn(n, row.notifRead)}
        />
      ))}
    </div>
  );
}

/* Everything a bell row needs, handed to all four groups verbatim — written
   once so the groups cannot come to disagree about what a row is, which is the
   defect 125 §6 closed one level down. `onActed` is the ONE thing the two
   surfaces answer differently: a row that navigates or joins closes the bell
   menu in one mount and the Daily Overview modal in the other. */
export type RowProps = Omit<
  React.ComponentProps<typeof NotifRowView>,
  "n" | "isRead"
> & {
  /** `isNotifRead` (utils.ts) — the ONE predicate the unread count also asks
   *  (brief 42 follow-on). Handed in rather than imported so this file states
   *  no opinion about read state. */
  isNotifReadFn: (
    n: NotifRowFace,
    notifRead: readonly unknown[] | null | undefined,
  ) => boolean;
  notifRead: readonly unknown[];
};

export default function NotifGroupedList({
  notifications,
  row,
}: {
  /** THE list — `notifications`, not a copy and not a concatenation. Each row
   *  states its place (209 §26), so the mounts no longer pass a `today`. */
  notifications: GroupedRow[];
  row: RowProps;
}) {
  const bucket = bellGroups(notifications);
  const { t } = row;
  return (
    <>
      <SEC
        title={t("notif.event.today", { fallback: "Today" })}
        color="var(--ac)"
        items={bucket.today}
        row={row}
      />
      <SEC
        title={t("app.notif.overdue", { fallback: "Overdue" })}
        color="var(--rd)"
        items={bucket.late}
        row={row}
      />
    </>
  );
}
